import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, Sliders } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

interface SpeakerToggleProps {
  /** Text to read aloud. If empty, the button is disabled. */
  text: string;
  /** Compact icon-only button (default true). */
  compact?: boolean;
  className?: string;
}

/**
 * Reusable speaker widget: ON/OFF, rate, pitch, voice filter.
 * Uses the browser SpeechSynthesis API (no API key, fully offline).
 */
export const SpeakerToggle = ({ text, compact = true, className }: SpeakerToggleProps) => {
  const [enabled, setEnabled] = useState(true);
  const [speaking, setSpeaking] = useState(false);
  const [rate, setRate] = useState(1);
  const [pitch, setPitch] = useState(1);
  const [volume, setVolume] = useState(1);
  const [voiceFilter, setVoiceFilter] = useState<"default" | "female" | "male">("default");
  const utterRef = useRef<SpeechSynthesisUtterance | null>(null);

  useEffect(() => {
    return () => {
      if (typeof window !== "undefined") window.speechSynthesis?.cancel();
    };
  }, []);

  const pickVoice = (): SpeechSynthesisVoice | undefined => {
    const voices = window.speechSynthesis.getVoices();
    if (voiceFilter === "default") return voices[0];
    const match = voices.find(v => v.name.toLowerCase().includes(voiceFilter));
    return match || voices[0];
  };

  const speak = () => {
    if (!enabled || !text.trim() || typeof window === "undefined") return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = rate;
    u.pitch = pitch;
    u.volume = volume;
    const voice = pickVoice();
    if (voice) u.voice = voice;
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    utterRef.current = u;
    window.speechSynthesis.speak(u);
    setSpeaking(true);
  };

  const stop = () => {
    window.speechSynthesis?.cancel();
    setSpeaking(false);
  };

  return (
    <div className={`inline-flex items-center gap-1 ${className || ""}`}>
      <Button
        type="button"
        variant="ghost"
        size={compact ? "icon" : "sm"}
        onClick={speaking ? stop : speak}
        disabled={!enabled || !text.trim()}
        aria-label={speaking ? "Stop speaking" : "Speak"}
        className={compact ? "h-8 w-8" : ""}
      >
        {speaking ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
      </Button>

      <Popover>
        <PopoverTrigger asChild>
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8" aria-label="Speaker settings">
            <Sliders className="w-3.5 h-3.5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 space-y-3" align="end">
          <div className="flex items-center justify-between">
            <Label htmlFor="speaker-on" className="text-xs">Speaker</Label>
            <Switch id="speaker-on" checked={enabled} onCheckedChange={setEnabled} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wider">Rate {rate.toFixed(1)}x</Label>
            <Slider value={[rate]} min={0.5} max={2} step={0.1} onValueChange={(v) => setRate(v[0])} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wider">Pitch {pitch.toFixed(1)}</Label>
            <Slider value={[pitch]} min={0.5} max={2} step={0.1} onValueChange={(v) => setPitch(v[0])} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wider">Volume {Math.round(volume * 100)}%</Label>
            <Slider value={[volume]} min={0} max={1} step={0.05} onValueChange={(v) => setVolume(v[0])} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wider">Voice filter</Label>
            <div className="flex gap-1">
              {(["default", "female", "male"] as const).map(opt => (
                <Button
                  key={opt}
                  type="button"
                  size="sm"
                  variant={voiceFilter === opt ? "default" : "outline"}
                  onClick={() => setVoiceFilter(opt)}
                  className="flex-1 text-[10px] capitalize h-7"
                >
                  {opt}
                </Button>
              ))}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default SpeakerToggle;
